import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import apiService from '../services/apiService';
import { LEGAL_VERSIONS } from '../utils/constants';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  email: string;
  phoneNumber?: string;
  authProvider?: string;
  profilePicture?: string;
  bio?: string;
  phoneVerified: boolean;
  status: string;
  driverRating?: number;
  passengerRating?: number;
  totalTripsAsDriver?: number;
  totalTripsAsPassenger?: number;
  role?: 'DRIVER' | 'PASSENGER' | 'ADMIN' | 'SUPER_ADMIN'; // Made optional

  // New driver verification fields
  isDriver?: boolean;
  driverStatus?: 'NOT_APPLIED' | 'PENDING' | 'AI_VERIFIED' | 'APPROVED' | 'REJECTED' | 'SUSPENDED';
  drivingLicenseUrl?: string;
  faceMatchScore?: number;
  licenseData?: any;
  driverApprovedAt?: string;
  driverRejectedAt?: string;
  driverRejectionReason?: string;
  isLicenseVerified?: boolean;
  licenseVerifiedAt?: string;
  stripeConnectOnboarded?: boolean;
  stripeConnectAccountId?: string;
  memberId?: string;

  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  googleLogin: (idToken: string) => Promise<{ success: boolean; message?: string; isNewUser?: boolean; pendingUser?: any; pendingToken?: string }>;
  appleLogin: (identityToken: string, fullName?: { givenName?: string; familyName?: string }) => Promise<{ success: boolean; message?: string; isNewUser?: boolean; pendingUser?: any; pendingToken?: string }>;
  register: (userData: RegisterData) => Promise<{ success: boolean; message?: string }>;
  logout: (sessionExpired?: boolean) => void;
  updateUser: (userData: Partial<User>) => Promise<void>;
  refreshUserData: () => Promise<void>;
  setAuthSession: (token: string, user: User) => void;
}

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber?: string;
  preferredName?: string;
  tosVersion?: string;
  privacyVersion?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored token on app start
    const storedToken = localStorage.getItem('rideclub_token');
    const storedUser = localStorage.getItem('rideclub_user');
    const appVersion = localStorage.getItem('rideclub_app_version');

    // Force re-login if app version changed (to clear old token format)
    if (appVersion !== '1.1.0') {
      localStorage.removeItem('rideclub_token');
      localStorage.removeItem('rideclub_user');
      localStorage.setItem('rideclub_app_version', '1.1.0');
      setIsLoading(false);
      return;
    }

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        apiService.setAuthHeader(storedToken);

        // Refresh user data in background
        refreshUserData().catch((error) => {
          // If token is invalid, silently clear auth without redirecting
          if (error?.response?.status === 401) {
            localStorage.removeItem('rideclub_token');
            localStorage.removeItem('rideclub_user');
            setToken(null);
            setUser(null);
            apiService.clearAuthHeader();
          }
        });
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('rideclub_token');
        localStorage.removeItem('rideclub_user');
      }
    }

    setIsLoading(false);
  }, []);

  // Listen for global auth expiration events
  useEffect(() => {
    const handleTokenExpired = () => {
      console.warn('Auth token expired. Logging out globally...');
      localStorage.removeItem('rideclub_token');
      localStorage.removeItem('rideclub_user');
      setToken(null);
      setUser(null);
      apiService.clearAuthHeader();
      
      const currentPath = window.location.pathname;
      const publicPaths = ['/', '/login', '/register', '/forgot-password', '/find-rides'];
      if (!publicPaths.includes(currentPath)) {
        window.location.href = `/login?session_expired=true&redirect=${encodeURIComponent(currentPath)}`;
      }
    };

    window.addEventListener('auth-token-expired', handleTokenExpired);
    return () => window.removeEventListener('auth-token-expired', handleTokenExpired);
  }, []);

  // Inactivity timeout auto-logout (30 minutes)
  useEffect(() => {
    if (!user) return;

    // Timeout duration: 30 minutes in milliseconds
    const INACTIVITY_TIMEOUT = 30 * 60 * 1000;
    let timeoutId: NodeJS.Timeout;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.warn('User session expired due to inactivity. Logging out...');
        logout(true);
      }, INACTIVITY_TIMEOUT);
    };

    // Events to monitor for activity
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    // Add general activity listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, resetTimer, { passive: true });
    });

    // Throttled mousemove to avoid high CPU overhead
    let lastMouseMove = 0;
    const handleMouseMove = () => {
      const now = Date.now();
      if (now - lastMouseMove > 5000) { // Throttle to once every 5 seconds
        lastMouseMove = now;
        resetTimer();
      }
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    // Initialize timer
    resetTimer();

    // Clean up
    return () => {
      clearTimeout(timeoutId);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [user]);

  const login = async (email: string, password: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await apiService.post('/auth/login', {
        email,
        password,
      });

      if (response.success) {
        const { token: authToken, user: userData } = response.data;

        // Store token and user data
        localStorage.setItem('rideclub_token', authToken);
        localStorage.setItem('rideclub_user', JSON.stringify(userData));

        // Update state
        setToken(authToken);
        setUser(userData);

        // Set auth header for future requests
        apiService.setAuthHeader(authToken);

        return { success: true };
      } else {
        return { success: false, message: response.message || 'Login failed' };
      }
    } catch (error: any) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed. Please try again.'
      };
    }
  };

  const googleLogin = async (idToken: string): Promise<{ success: boolean; message?: string; isNewUser?: boolean; pendingUser?: any; pendingToken?: string }> => {
    try {

      const response = await apiService.post('/auth/google', {
        idToken,
        tosVersion: LEGAL_VERSIONS.TOS,
        privacyVersion: LEGAL_VERSIONS.PRIVACY,
      });

      if (response.success) {
        const { token: authToken, user: userData, isNewUser } = response.data;

        if (isNewUser) {
          apiService.setAuthHeader(authToken);
          return { success: true, isNewUser: true, pendingUser: userData, pendingToken: authToken };
        }

        localStorage.setItem('rideclub_token', authToken);
        localStorage.setItem('rideclub_user', JSON.stringify(userData));

        setToken(authToken);
        setUser(userData);
        apiService.setAuthHeader(authToken);

        return { success: true, isNewUser: false };
      } else {
        return { success: false, message: response.message || 'Google Login failed' };
      }
    } catch (error: any) {
      console.error('Google Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Google Login failed. Please try again.'
      };
    }
  };

  const appleLogin = async (identityToken: string, fullName?: { givenName?: string; familyName?: string }): Promise<{ success: boolean; message?: string; isNewUser?: boolean; pendingUser?: any; pendingToken?: string }> => {
    try {
      const response = await apiService.post('/auth/apple', {
        identityToken,
        fullName,
        tosVersion: LEGAL_VERSIONS.TOS,
        privacyVersion: LEGAL_VERSIONS.PRIVACY,
      });

      if (response.success) {
        const { token: authToken, user: userData, isNewUser } = response.data;

        if (isNewUser) {
          apiService.setAuthHeader(authToken);
          return { success: true, isNewUser: true, pendingUser: userData, pendingToken: authToken };
        }

        localStorage.setItem('rideclub_token', authToken);
        localStorage.setItem('rideclub_user', JSON.stringify(userData));

        setToken(authToken);
        setUser(userData);
        apiService.setAuthHeader(authToken);

        return { success: true, isNewUser: false };
      } else {
        return { success: false, message: response.message || 'Apple Login failed' };
      }
    } catch (error: any) {
      console.error('Apple Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Apple Login failed. Please try again.'
      };
    }
  };

  const register = async (userData: RegisterData): Promise<{ success: boolean; message?: string }> => {
    try {

      console.log('--- REGISTER API CALL START ---');
      console.log('Sending userData:', { ...userData, password: '[HIDDEN]' });

      const response = await apiService.post('/auth/register', userData);

      console.log('--- REGISTER API Call Response ---');
      console.log('Raw response:', response);

      if (response.success) {
        // Registration successful. Do NOT auto-login.
        // The user must verify their email before logging in.
        return { success: true, message: 'Account created successfully. Please check your email to verify your account.' };
      } else {
        return { success: false, message: response.message || 'Registration failed' };
      }
    } catch (error: any) {
      console.error('--- REGISTER API ERROR ---');
      console.error('Registration error details:', error);
      console.error('Error response data:', error.response?.data);

      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed. Please try again.'
      };
    }
  };

  const logout = (sessionExpired = false) => {
    // Clear stored data
    localStorage.removeItem('rideclub_token');
    localStorage.removeItem('rideclub_user');

    // Clear state
    setToken(null);
    setUser(null);

    // Clear auth header
    apiService.clearAuthHeader();

    // Only redirect to login if not already on a public page
    const currentPath = window.location.pathname;
    const publicPaths = ['/', '/login', '/register', '/forgot-password', '/find-rides'];

    if (!publicPaths.includes(currentPath)) {
      window.location.href = sessionExpired
        ? `/login?session_expired=true&redirect=${encodeURIComponent(currentPath)}`
        : '/login';
    }
  };

  const updateUser = async (userData: Partial<User>) => {
    try {
      const response = await apiService.patch('/auth/profile', userData);

      if (response.success) {
        const updatedUser = { ...user, ...response.data };
        setUser(updatedUser);
        localStorage.setItem('rideclub_user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  };

  const refreshUserData = useCallback(async () => {
    try {
      const response = await apiService.get('/auth/profile');

      if (response.success) {
        // Backend returns { data: { user } }
        const refreshedUser = (response as any).data?.user || (response as any).data || null;
        if (refreshedUser) {
          setUser(refreshedUser);
          localStorage.setItem('rideclub_user', JSON.stringify(refreshedUser));
        }
      }
    } catch (error) {
      console.error('Refresh user data error:', error);
      // Don't throw error to avoid disrupting app flow
    }
  }, []);

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    login,
    googleLogin,
    appleLogin,
    register,
    logout,
    updateUser,
    refreshUserData,
    setAuthSession: (authToken: string, userData: User) => {
      localStorage.setItem('rideclub_token', authToken);
      localStorage.setItem('rideclub_user', JSON.stringify(userData));
      setToken(authToken);
      setUser(userData);
      apiService.setAuthHeader(authToken);
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};